import React, {lazy} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';

import {getFeatureFlagStatus} from 'client/components/FeatureFlag/featureFlag.utils';
import sessionStore from 'client/Session/SessionStore';

import {
  createRoute,
  redirect,
  redirectRoute,
  layoutComponent,
  withRouter
} from 'client/routeHelpers';

import ClassroomsChromeWrapper from './ClassroomsChromeWrapper';

const ClassroomsList = lazy(
  () => import('client/Classrooms/Teacher/ClassroomsList/ClassroomsList.react')
);
const Classroom = lazy(() => import('client/Classrooms/Classroom/Classroom.react'));

const TeacherDashboard = lazy(
  () => import('client/Classrooms/Classroom/Teacher/TeacherDashboard/TeacherDashboard')
);

const People = lazy(() => import('client/Classrooms/Classroom/Teacher/People/People.react'));
const Coteachers = lazy(
  () => import('client/Classrooms/Classroom/Teacher/People/Coteachers/Coteachers.react')
);

const ManageRoster = lazy(
  () => import('client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.react')
);
const EnrolledStudents = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/ManageRoster/EnrolledStudents/EnrolledStudents.react')
);
const InvitedStudents = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/ManageRoster/InvitedStudents/InvitedStudents.react')
);
const TeacherClassroomSettings = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/TeacherClassroomSettings/TeacherClassroomSettings.react')
);

// @todo make separate student list components for Active Assignments and Past Assignments
const AssignmentsList = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentsList.react')
);

const StudentAssignmentsList = lazy(
  () => import('client/Classrooms/Classroom/Student/Assignments/StudentAssignmentsListV2')
);

const TeacherSubjectsList = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react')
);

// v2-only view; when the v2 dashboard is on, ClassroomsChromeWrapper renders it inline instead.
// This registered route is what keeps /classes/:id/skill-paths resolving (and the flag-off fallback).
const TeacherSkillPathsList = lazy(() =>
  import('client/TeacherDashboard/SkillPaths/SkillPathsList').then((module) => ({
    default: module.SkillPathsList
  }))
);

function ClassSkillPaths({params}) {
  return <TeacherSkillPathsList classroomId={params?.classId} />;
}
ClassSkillPaths.propTypes = {
  params: PropTypes.object
};

const SingleStudentView = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/SingleStudentView.react')
);
const StudentOverview = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Overview/StudentOverview.react')
);
const StudentAssignmentOverview = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Assignments/AssignmentOverview')
);
const StudentTopics = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Topics/Topics')
);
const StudentInfo = lazy(
  () => import('client/Classrooms/Classroom/Teacher/Student/Info/StudentInfo.react')
);
const ApprovalStatuses = lazy(() => import('./Teacher/ApprovalStatuses/ApprovalStatuses.react'));

function requireTeacherStatus() {
  if (!sessionStore.hasTeacherAccess()) {
    throw redirect({to: '/'});
  }
}

// The v2 dashboard folds the class list into the sidebar, so its list pages bounce home
// (the inverse of homeBeforeLoad, which sends flag-off teachers /home → /classes).
async function redirectDashboardTeachersHome() {
  if (!sessionStore.isTeacher()) {
    return;
  }
  const isEnabled = await getFeatureFlagStatus('teacher_dashboard_v2');
  if (isEnabled) {
    throw redirect({to: '/home'});
  }
}

function renderComponentWithStatuses(Component) {
  function ComponentWithStatuses(props) {
    return (
      <>
        <ApprovalStatuses
          className={classnames('max-w-[1280px]', {
            'max-w-[1088px]': props.route.path === 'insights' || props.route.path === 'overview'
          })}
          type='neutral'
        />
        <Component {...props} />
      </>
    );
  }

  ComponentWithStatuses.propTypes = {
    route: PropTypes.object
  };

  return ComponentWithStatuses;
}

const classroomSessionRequired = requireSessionWithLoginPrompt()(ClassroomsChromeWrapper);

export function makeClassesRoute(parentRoute) {
  // Top: /classes — session-wrapped ClassroomsChromeWrapper layout; non-teachers redirected to /home.
  const classesRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'classes',
    staticData: {title: 'Classes | Albert.io'},
    component: layoutComponent(classroomSessionRequired),
    beforeLoad: () => {
      if (!sessionStore.isTeacher()) {
        throw redirect({to: '/home'});
      }
    }
  });

  // IndexRoute → ClassroomsList
  const indexRoute = createRoute({
    getParentRoute: () => classesRoute,
    path: '/',
    staticData: {withWhiteBackground: true},
    component: withRouter(renderComponentWithStatuses(ClassroomsList)),
    beforeLoad: redirectDashboardTeachersHome
  });

  // /classes/archived
  const archivedRoute = createRoute({
    getParentRoute: () => classesRoute,
    path: 'archived',
    staticData: {title: 'Archived', withWhiteBackground: true},
    component: withRouter(renderComponentWithStatuses(ClassroomsList)),
    beforeLoad: async () => {
      requireTeacherStatus();
      await redirectDashboardTeachersHome();
    }
  });

  // /classes/$classId — layout
  const classroomRoute = createRoute({
    getParentRoute: () => classesRoute,
    path: '$classId',
    component: layoutComponent(Classroom)
  });

  // /classes/$classId/my-assignments
  const myAssignmentsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'my-assignments',
    component: withRouter(StudentAssignmentsList)
  });

  // /classes/$classId/assignments — layout
  const assignmentsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'assignments',
    component: layoutComponent(renderComponentWithStatuses(AssignmentsList)),
    beforeLoad: requireTeacherStatus
  });
  const assignmentsTemplatesRedirect = redirectRoute({
    getParentRoute: () => assignmentsRoute,
    path: 'templates',
    to: '/saved'
  });

  // Legacy redirects under $classId
  const myExamsRedirect = redirectRoute({
    getParentRoute: () => classroomRoute,
    path: 'my-exams',
    to: '/classes/$classId/my-assignments'
  });
  const myAssessmentsRedirect = redirectRoute({
    getParentRoute: () => classroomRoute,
    path: 'my-assessments',
    to: '/classes/$classId/my-assignments'
  });
  const assessmentsRedirect = redirectRoute({
    getParentRoute: () => classroomRoute,
    path: 'assessments',
    to: '/classes/$classId/assignments'
  });
  const examsRedirect = redirectRoute({
    getParentRoute: () => classroomRoute,
    path: 'exams',
    to: '/classes/$classId/assignments'
  });

  // /classes/$classId/people — layout
  const peopleRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'people',
    staticData: {withWhiteBackground: true},
    component: layoutComponent(renderComponentWithStatuses(People)),
    beforeLoad: requireTeacherStatus
  });
  const peopleIndexRedirect = redirectRoute({
    getParentRoute: () => peopleRoute,
    path: '/',
    to: '/classes/$classId/people/students/enrolled'
  });
  // Pathless layout for ManageRoster — children paths relative to `people`.
  const manageRosterRoute = createRoute({
    getParentRoute: () => peopleRoute,
    id: 'manage-roster',
    component: layoutComponent(ManageRoster)
  });
  const enrolledStudentsRoute = createRoute({
    getParentRoute: () => manageRosterRoute,
    path: 'students/enrolled',
    staticData: {title: 'Enrolled', withWhiteBackground: true},
    component: withRouter(EnrolledStudents)
  });
  const invitedStudentsRoute = createRoute({
    getParentRoute: () => manageRosterRoute,
    path: 'students/invited',
    staticData: {title: 'Invited', withWhiteBackground: true},
    component: withRouter(InvitedStudents)
  });
  const coteachersRoute = createRoute({
    getParentRoute: () => peopleRoute,
    path: 'coteachers/approved',
    staticData: {title: 'Coteachers', withWhiteBackground: true},
    component: withRouter(Coteachers)
  });

  // /classes/$classId/subjects
  const subjectsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'subjects',
    staticData: {withWhiteBackground: true},
    component: withRouter(renderComponentWithStatuses(TeacherSubjectsList)),
    beforeLoad: requireTeacherStatus
  });

  // /classes/$classId/skill-paths
  const skillPathsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'skill-paths',
    component: layoutComponent(renderComponentWithStatuses(ClassSkillPaths)),
    beforeLoad: requireTeacherStatus
  });

  // /classes/$classId/students — container (no component)
  const studentsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'students',
    beforeLoad: requireTeacherStatus
  });
  const studentsIndexRedirect = redirectRoute({
    getParentRoute: () => studentsRoute,
    path: '/',
    to: '/classes/$classId/students/overview'
  });
  const studentsOverviewRoute = createRoute({
    getParentRoute: () => studentsRoute,
    path: 'overview',
    component: withRouter(renderComponentWithStatuses(TeacherDashboard))
  });
  // /classes/$classId/students/detail/$userId — layout
  const studentDetailRoute = createRoute({
    getParentRoute: () => studentsRoute,
    path: 'detail/$userId',
    component: layoutComponent(SingleStudentView)
  });
  const studentDetailIndexRedirect = redirectRoute({
    getParentRoute: () => studentDetailRoute,
    path: '/',
    to: '/classes/$classId/students/detail/$userId/overview'
  });
  const studentInfoRoute = createRoute({
    getParentRoute: () => studentDetailRoute,
    path: 'info',
    component: withRouter(StudentInfo)
  });
  const studentAssignmentsRoute = createRoute({
    getParentRoute: () => studentDetailRoute,
    path: 'assignments',
    component: withRouter(StudentAssignmentOverview)
  });
  const studentOverviewRoute = createRoute({
    getParentRoute: () => studentDetailRoute,
    path: 'overview',
    component: withRouter(StudentOverview)
  });
  const studentTopicsRoute = createRoute({
    getParentRoute: () => studentDetailRoute,
    path: 'topics',
    component: withRouter(StudentTopics)
  });

  // /classes/$classId/settings — container (no component)
  const settingsRoute = createRoute({
    getParentRoute: () => classroomRoute,
    path: 'settings',
    beforeLoad: requireTeacherStatus
  });
  const settingsIndexRedirect = redirectRoute({
    getParentRoute: () => settingsRoute,
    path: '/',
    to: '/classes/$classId/settings/details'
  });
  const settingsDetailsRoute = createRoute({
    getParentRoute: () => settingsRoute,
    path: 'details',
    component: withRouter(renderComponentWithStatuses(TeacherClassroomSettings))
  });

  studentDetailRoute.addChildren([
    studentDetailIndexRedirect,
    studentInfoRoute,
    studentAssignmentsRoute,
    studentOverviewRoute,
    studentTopicsRoute
  ]);
  studentsRoute.addChildren([studentsIndexRedirect, studentsOverviewRoute, studentDetailRoute]);
  settingsRoute.addChildren([settingsIndexRedirect, settingsDetailsRoute]);

  manageRosterRoute.addChildren([enrolledStudentsRoute, invitedStudentsRoute]);
  peopleRoute.addChildren([peopleIndexRedirect, manageRosterRoute, coteachersRoute]);

  assignmentsRoute.addChildren([assignmentsTemplatesRedirect]);

  classroomRoute.addChildren([
    myAssignmentsRoute,
    assignmentsRoute,
    myExamsRedirect,
    myAssessmentsRedirect,
    assessmentsRedirect,
    examsRedirect,
    peopleRoute,
    subjectsRoute,
    skillPathsRoute,
    studentsRoute,
    settingsRoute
  ]);

  return classesRoute.addChildren([indexRoute, archivedRoute, classroomRoute]);
}
